import type { Theme } from "./theme-context";

// Page background per theme (--background in src/index.css), mirrored by the
// theme-color metas in index.html.
const THEME_COLORS = { light: "#f4f3f0", dark: "#10171f" } as const;

// While the launch splash covers the app the chrome keeps the splash colour;
// theme changes made meanwhile are remembered and applied on release.
let heldColor: string | null = null;
let lastTheme: Theme = "system";

const paint = (color: (meta: HTMLMetaElement) => string): void => {
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => {
      meta.content = color(meta);
    });
};

/**
 * Point the browser/OS chrome colour (theme-color) at the page background. The
 * metas follow the OS scheme by default; a theme picked in the app overrides
 * both, so the status bar never shows the other theme's colour.
 */
export const syncThemeColor = (theme: Theme): void => {
  lastTheme = theme;
  if (heldColor) return;
  paint((meta) => {
    const scheme =
      theme === "system"
        ? meta.media.includes("dark")
          ? "dark"
          : "light"
        : theme;
    return THEME_COLORS[scheme];
  });
};

/** Paint the browser/OS chrome (theme-color) one colour, whatever the scheme. */
export const setThemeColor = (color: string): void => {
  paint(() => color);
};

/** Pin the chrome to `color` (the launch splash) until releaseThemeColor(). */
export const holdThemeColor = (color: string): void => {
  heldColor = color;
  setThemeColor(color);
};

/** Drop the pin and repaint the chrome for the latest theme. */
export const releaseThemeColor = (): void => {
  if (!heldColor) return;
  heldColor = null;
  syncThemeColor(lastTheme);
};
